import {inject, Injectable} from '@angular/core';
import {ViewportScroller} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  private router = inject(Router);

  constructor() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.router.parseUrl(this.router.url).fragment;
        if (fragment) {
          // Delay to ensure DOM is ready
          setTimeout(() => this.scrollToSection(fragment, 80), 500);
        }
      });
  }

  scrollToSection(sectionId: string, offset: number = 80): void {
    const id = sectionId.replace('#', '');

    const element = document.getElementById(id);
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetPosition = rect.top + scrollTop - offset;

    this.smoothScrollTo(targetPosition);
  }

  scrollToTop(): void {
    this.smoothScrollTo(0);
  }

  private smoothScrollTo(targetPosition: number): void {
    // Safari on iOS < 15.4 doesn't support smooth scrolling via window.scrollTo
    // Use a manual animation as fallback
    const supportsNativeSmooth = 'scrollBehavior' in document.documentElement.style;

    if (supportsNativeSmooth) {
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    } else {
      const start = window.pageYOffset || document.documentElement.scrollTop;
      const distance = targetPosition - start;
      const duration = 500;
      let startTime: number | null = null;

      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 0.5 - Math.cos(progress * Math.PI) / 2;
        window.scrollTo(0, start + distance * ease);
        if (elapsed < duration) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    }
  }

}
